import React, { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import './LoginPage.css'

interface LoginPageProps {
  onClose: () => void;
  initialMode?: 'login' | 'signup';
}

interface FirebaseAuthError {
  code?: string;
  message?: string;
}

const getReadableAuthError = (error: FirebaseAuthError, fallbackPrefix: string) => {
  const code = error.code ?? '';

  if (code === 'auth/configuration-not-found') {
    return 'Google Sign-In is not configured for this Firebase project. Enable Google in Firebase Console > Authentication > Sign-in method, then add this app domain in Authentication > Settings > Authorized domains.'
  }

  if (code === 'auth/unauthorized-domain') {
    return 'This domain is not authorized for Firebase Auth. Add the current domain in Firebase Console > Authentication > Settings > Authorized domains.'
  }

  return `${fallbackPrefix}: ${error.message || 'Something went wrong'}`
}

const LoginPage: React.FC<LoginPageProps> = ({ onClose, initialMode = 'login' }) => {
  const [isLogin, setIsLogin] = useState(initialMode === 'login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')

  const { signIn, signUp, signInWithGoogle } = useAuth()

  // Update mode when initialMode prop changes
  React.useEffect(() => {
    setIsLogin(initialMode === 'login')
    setError('')
    setMessage('')
    setConfirmPassword('')
  }, [initialMode])

  const handleGoogleSignIn = async () => {
    setLoading(true)
    setError('')
    setMessage('')

    try {
      const { error } = await signInWithGoogle()
      if (error) {
        setError(getReadableAuthError(error, 'Google sign in failed'))
        setLoading(false)
      }
      // Note: On success, the auth provider flow handles redirecting.
    } catch (err: any) {
      setError(getReadableAuthError(err, 'Unexpected error'))
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setMessage('')

    try {
      if (isLogin) {
        const { error } = await signIn(email, password)
        if (error) {
          setError(`Login failed: ${error.message}`)
        } else {
          setMessage('Login successful! Redirecting to console...')
        }
      } else {
        // Validate password match
        if (password !== confirmPassword) {
          setError('Passwords do not match')
          setLoading(false)
          return
        }

        const { error } = await signUp(email, password)
        if (error) {
          setError(`Sign up failed: ${error.message}`)
        } else {
          setMessage('Account created! Please check your email and click the confirmation link, then you can sign in.')
        }
      }
    } catch (err: any) {
      setError(`Unexpected error: ${err.message || 'Something went wrong'}`)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <button className="close-button" onClick={onClose} type="button">×</button>
        <div className="login-content">
          <div className="login-header">
            <p className="login-kicker">{isLogin ? 'Welcome back' : 'Get started'}</p>
            <h1>{isLogin ? 'Sign in to your console' : 'Create your Novembr account'}</h1>
            <p className="login-subtitle">
              {isLogin
                ? 'Manage moderation, keys, analytics, and billing from one place.'
                : 'Start moderating content in minutes with your new Novembr workspace.'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="Enter your email"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter your password"
              />
            </div>

            {!isLogin && (
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  placeholder="Confirm your password"
                />
              </div>
            )}

            {error && <div className="error-message">{error}</div>}
            {message && <div className="success-message">{message}</div>}

            {!isLogin && (
              <div className="terms-text">
                <p>
                  By signing up, you agree to our <a href="/terms" target="_blank" rel="noopener noreferrer" className="link">Terms of Service</a> and <a href="/privacy" target="_blank" rel="noopener noreferrer" className="link">Privacy Policy</a>.
                </p>
              </div>
            )}

            <button type="submit" disabled={loading} className="submit-button">
              {loading ? 'Loading...' : (isLogin ? 'Sign In' : 'Sign Up')}
            </button>

            <div className="divider">
              <span>or</span>
            </div>

            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={loading}
              className="google-button"
            >
              <svg className="google-icon" viewBox="0 0 24 24" width="20" height="20">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Continue with Google
            </button>
          </form>

          <div className="form-footer">
            <p>
              {isLogin ? "Don't have an account?" : 'Already have an account?'}
              <button
                type="button"
                onClick={() => {
                  setIsLogin(!isLogin)
                  setError('')
                  setMessage('')
                  setConfirmPassword('')
                }}
                className="toggle-button"
              >
                {isLogin ? 'Sign Up' : 'Sign In'}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LoginPage
